import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const karyotyping: ContentBlock<"procedure"> = {
  id: makeId("proc-karyotyping"),
  kind: "procedure",
  tags: ["genetic-screening", "both-partners", "lab"],
  title: "Karyotyping",
  subtitle:
    "Chromosome analysis to detect structural or numerical abnormalities",
  body: {
    procedureName: "Karyotyping",
    category: "diagnostic",
    description:
      "A blood test that examines your chromosomes under a microscope to detect missing, extra, or rearranged chromosomes. Karyotype abnormalities can contribute to infertility, recurrent miscarriage, or genetic conditions in offspring.",
    howItWorks:
      "A blood sample is drawn from your arm. In the lab, white blood cells are cultured and stimulated to divide. The chromosomes are then stained, photographed, and arranged into pairs to create a karyotype image. A geneticist examines the arrangement for any numerical abnormalities (like an extra or missing chromosome) or structural changes (translocations, inversions, deletions). Results are used to guide treatment decisions, including whether preimplantation genetic testing (PGT) is recommended.",
    duration: "5–10 minutes for the blood draw; results take 2–3 weeks",
    anesthesia: "none",
    whatToExpect: [
      "A standard blood draw, quick and straightforward",
      "Results take longer than most blood tests (2–3 weeks) because cells need to be cultured",
      "A genetic counselor or your doctor will explain the results",
      "If a translocation or other finding is identified, PGT-SR may be recommended for IVF embryos",
    ],
    results: [
      {
        label: "When you hear",
        detail:
          "Karyotype results usually take 2-3 weeks because cells need to grow in culture before chromosomes can be analyzed.",
      },
      {
        label: "What you learn",
        detail:
          "The report looks for missing, extra, or rearranged chromosomes, including balanced translocations.",
      },
      {
        label: "Follow-up",
        detail:
          "If a structural chromosome finding is present, your team may recommend genetic counseling and PGT-SR for IVF embryos.",
      },
    ],
    prepInstructions: [
      "No fasting or special preparation required",
      "Both partners may be tested, especially after recurrent pregnancy loss",
      "Bring a list of any known genetic conditions in your family",
    ],
    risks: [
      "Minor bruising at the blood draw site",
      "No risks beyond a standard blood test",
    ],
    forWhom: "both",
    frBody: {
      procedureName: "Caryotype",
      description:
        "Analyse sanguine examinant les chromosomes au microscope pour détecter des chromosomes manquants, surnuméraires ou réarrangés. Les anomalies du caryotype peuvent être responsables d'infertilité, de fausses couches à répétition ou de pathologies génétiques chez l'enfant.",
      howItWorks:
        "Une prise de sang est effectuée au bras. Au laboratoire, les globules blancs sont mis en culture et stimulés pour se diviser. Les chromosomes sont ensuite colorés, photographiés et appariés pour réaliser une image du caryotype. Un généticien examine l'arrangement pour détecter d'éventuelles anomalies de nombre (chromosome surnuméraire ou manquant) ou de structure (translocations, inversions, délétions). Les résultats orientent les décisions thérapeutiques, notamment l'indication d'un diagnostic préimplantatoire (DPI/PGT).",
      duration:
        "5 à 10 minutes pour la prise de sang ; résultats sous 2 à 3 semaines",
      whatToExpect: [
        "Prise de sang standard, rapide et simple",
        "Les résultats prennent plus de temps qu'une analyse habituelle (2 à 3 semaines), le temps de la culture cellulaire",
        "Un généticien ou votre médecin vous expliquera les résultats",
        "En cas de translocation ou autre anomalie, un DPI-SR (PGT-SR) peut être proposé sur les embryons en FIV (fécondation in vitro)",
      ],
      prepInstructions: [
        "Aucun jeûne ni préparation particulière",
        "Les deux partenaires peuvent être testés, notamment en cas de fausses couches à répétition",
        "Apportez la liste des pathologies génétiques connues dans votre famille",
      ],
      risks: [
        "Léger hématome au point de prélèvement",
        "Aucun risque au-delà d'une prise de sang standard",
      ],
    },
  },
  sources: [
    {
      title: "Recurrent pregnancy loss: a committee opinion",
      authors: "Practice Committee of the ASRM",
      journal: "ASRM Practice Committee",
      year: 2026,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/recurrent-pregnancy-loss-a-committee-opinion-2026/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(karyotyping);

registerRelationship({
  from: karyotyping.id,
  to: makeId("test-karyotype"),
  type: "related-to",
  label: "Canonical test",
});
